import React, { PropsWithChildren, useMemo, useState } from "react";
import { useInsightsContext } from "../../Context/InsightsContext";
import { usePaginationHelper } from "../../../../lib/hooks/usePaginationHelper";

import { GeneralStatsByRoomsResponse } from "../../../../api/grpc/insights/insights";
import { PaginationContext } from "../../../../lib/context/Pagination/PaginationContext";
import { trimText } from "../../../../lib/helpers/trimText";
import { Pagination } from "../../../shared/Pagination/Pagination";
import {
  StructureTable,
  StructureTableColumn,
  StructureTableRows,
} from "../../../shared/StructureTable/StructureTable";
import {
  sortStructureTableData,
  SortDirection,
} from "../../../shared/StructureTable/sortUtils";

import "./InsightsExpandedRooms.scss";

const columns: StructureTableColumn[] = [
  {
    key: "room_name",
    header: "Room name",
    sortable: true,
    sortKey: "name",
  },
  {
    key: "room_meetings",
    header: "Meetings",
    sortable: true,
    sortKey: "totalMeetings",
  },
  {
    key: "avg_daily_meetings",
    header: "Avg. daily meetings",
    sortable: true,
    sortKey: "avgMeetings",
  },
  {
    key: "avg_meetings_duration",
    header: "Avg. meeting duration (min)",
    sortable: true,
    sortKey: "avgMeetingTime",
  },
  {
    key: "occupancy",
    header: "Occupancy",
    sortable: true,
    sortKey: "occupancy",
  },
  {
    key: "no_shows",
    header: "No-shows",
    sortable: true,
    sortKey: "noShows",
  },
];

export const InsightsExpandedRooms = ({
  data,
  showPagination,
}: PropsWithChildren<{
  data: GeneralStatsByRoomsResponse;
  showPagination: boolean;
}>) => {
  const { page, pageSize, pagination } = usePaginationHelper(data.data.length);
  const { setInsightsLocation } = useInsightsContext();
  const [sortedColumnKey, setSortedColumnKey] = useState<string>("room_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const indexOfLastRoom = page * pageSize;
  const indexOfFirstRoom = indexOfLastRoom - pageSize;
  const sortedRoomData = useMemo(
    () =>
      sortStructureTableData(
        data.data,
        columns,
        sortedColumnKey,
        sortDirection
      ),
    [data.data, sortedColumnKey, sortDirection]
  );
  const currentRoomData = useMemo(
    () => sortedRoomData.slice(indexOfFirstRoom, indexOfLastRoom),
    [indexOfFirstRoom, indexOfLastRoom, sortedRoomData]
  );

  const rows: StructureTableRows[] = currentRoomData.map((item) => {
    // we check if > 0 to handle the cases if the values are negative
    return {
      room_name: (
        <h6
          className="mb-0"
          title={item.name}
          onClick={() => {
            setInsightsLocation({
              locationId: item.id,
              locationName: item.name,
              locationType: "Room",
            });
          }}
        >
          {trimText(item.name, 27)}
        </h6>
      ),
      room_meetings: <h6>{item.totalMeetings}</h6>,
      avg_daily_meetings: (
        <h6>{item.avgMeetings > 0 ? Math.round(item.avgMeetings) : 0}</h6>
      ),
      avg_meetings_duration: (
        <h6>{item.avgMeetingTime > 0 ? Math.round(item.avgMeetingTime) : 0}</h6>
      ),
      occupancy: (
        <h6>{item.occupancy > 0 ? Math.round(item.occupancy) + "%" : 0}</h6>
      ),
      no_shows: <h6>{item.noShows > 0 ? Math.round(item.noShows) : 0}</h6>,
    };
  });

  const handleSort = (columnKey: string) => {
    if (pagination.page !== 1) {
      pagination.setPage(1);
    }

    if (columnKey === sortedColumnKey) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }

    setSortedColumnKey(columnKey);
    setSortDirection("desc");
  };

  return (
    <PaginationContext.Provider value={pagination}>
      <StructureTable
        rows={rows}
        columns={columns}
        className="StructureTable--rooms InsightsExpandedRooms mt-5"
        onSort={handleSort}
        sortedColumnKey={sortedColumnKey}
        sortDirection={sortDirection}
      />

      {showPagination && <Pagination />}
    </PaginationContext.Provider>
  );
};
